/**
 * TenantContext
 *
 * Global org + site selection for the portal. Replaces the per-wizard-step
 * state that previously lived inside WorkflowPage.jsx.
 *
 * The selected org/site is persisted in localStorage so a page refresh
 * keeps the user in the same tenant.
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import organizationService from '../services/organizationService';

const STORAGE_KEY = 'davinci.tenant';

const TenantContext = createContext(null);

function loadInitial() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return { orgId: null, siteId: null };
    const parsed = JSON.parse(raw);
    return {
      orgId: parsed.orgId ?? null,
      siteId: parsed.siteId ?? null,
    };
  } catch {
    return { orgId: null, siteId: null };
  }
}

export function TenantProvider({ children }) {
  const [{ orgId, siteId }, setState] = useState(loadInitial);
  const [organizations, setOrganizations] = useState([]);
  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(false);

  // Persist selection
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ orgId, siteId }));
    } catch {
      // ignore
    }
  }, [orgId, siteId]);

  // Load organizations once (use AbortController to prevent React StrictMode double-fetch)
  useEffect(() => {
    const abortController = new AbortController();
    setLoading(true);
    organizationService
      .listOrganizations(0, 200, abortController.signal)
      .then((data) => {
        if (abortController.signal.aborted) return;
        const list = data?.content || data?.items || data || [];
        const allOrgs = Array.isArray(list) ? list : [];
        const activeOrgs = allOrgs.filter(
          (o) => !o.status || o.status.toUpperCase() === 'ACTIVE'
        );
        setOrganizations(activeOrgs);
        // Auto-select first org if none selected
        if (!orgId && activeOrgs.length > 0) {
          setState((s) => ({ ...s, orgId: activeOrgs[0].orgId ?? activeOrgs[0].id }));
        }
      })
      .catch(() => {
        if (!abortController.signal.aborted) setOrganizations([]);
      })
      .finally(() => {
        if (!abortController.signal.aborted) setLoading(false);
      });
    return () => {
      abortController.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load sites whenever org changes
  useEffect(() => {
    if (!orgId) {
      setSites([]);
      return;
    }
    const abortController = new AbortController();
    organizationService
      .listSites(orgId, 0, 200, abortController.signal)
      .then((data) => {
        if (abortController.signal.aborted) return;
        const list = data?.content || data?.items || data || [];
        setSites(Array.isArray(list) ? list : []);
      })
      .catch(() => {
        if (!abortController.signal.aborted) setSites([]);
      });
    return () => {
      abortController.abort();
    };
  }, [orgId]);

  const setOrgId = useCallback((nextOrgId) => {
    setState({ orgId: nextOrgId ?? null, siteId: null });
  }, []);

  const setSiteId = useCallback((nextSiteId) => {
    setState((s) => ({ ...s, siteId: nextSiteId ?? null }));
  }, []);

  const refreshSites = useCallback(async () => {
    if (!orgId) return [];
    const data = await organizationService.listSites(orgId, 0, 200);
    const list = data?.content || data?.items || data || [];
    const arr = Array.isArray(list) ? list : [];
    setSites(arr);
    return arr;
  }, [orgId]);

  const currentOrg = useMemo(
    () => organizations.find((o) => (o.orgId ?? o.id) === orgId) || null,
    [organizations, orgId]
  );
  const currentSite = useMemo(
    () => sites.find((s) => (s.siteId ?? s.id) === siteId) || null,
    [sites, siteId]
  );

  const value = useMemo(
    () => ({
      orgId,
      siteId,
      organizations,
      sites,
      currentOrg,
      currentSite,
      loading,
      setOrgId,
      setSiteId,
      refreshSites,
    }),
    [orgId, siteId, organizations, sites, currentOrg, currentSite, loading, setOrgId, setSiteId, refreshSites]
  );

  return <TenantContext.Provider value={value}>{children}</TenantContext.Provider>;
}

export function useTenant() {
  const ctx = useContext(TenantContext);
  if (!ctx) throw new Error('useTenant must be used within a TenantProvider');
  return ctx;
}

export default TenantContext;

